import React, { useState, useEffect, useRef } from 'react';
import '../styles/Main.css';
import { gsap } from 'gsap';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import playerstatMain from '../assets/images/playerstat_main.png';
import playerstatLogo from '../assets/images/playerstat_logo.png';
import playerstatText from '../assets/images/playerstat_text.svg';
import playerstatSecond from '../assets/images/playerstat_second.png';
import playerstatThird from '../assets/images/playerstat_third.png';
import playerstatFourth from '../assets/images/playerstat_fourth.png';
import playerstatFifth from '../assets/images/playerstat_fifth.png';
import playerstatSixth from '../assets/images/playerstat_sixth.png';
import playerstatSeventh from '../assets/images/playerstat_seventh.png';
import playerstatEighth from '../assets/images/playerstat_eighth.png';
import playerstatNineth from '../assets/images/playerstat_nineth.png';
import playerstatTenth from '../assets/images/playerstat_tenth.png';
import playerstatEleventh from '../assets/images/playerstat_eleventh.png';
import playerstatTwelveth from '../assets/images/playerstat_twelveth.png';
import playerstatThirteenth from '../assets/images/playerstat_thirteenth.png';
import playerstatFourteenth from '../assets/images/playerstat_fourteenth.png';
import playerstatFifteenth from '../assets/images/playerstat_fifteenth.png';
import playerstatSixteenth from '../assets/images/playerstat_sixteenth.png';
import playerstatSeventeenth from '../assets/images/playerstat_seventeenth.png';
import playerstatEighteenth from '../assets/images/playerstat_eighteenth.png';
import playerstatTwenty from '../assets/images/playerstat_twenty.png';
import playerstatTwentyOne from '../assets/images/playerstat_twenty_one.png';
import playerstatTwentyTwo from '../assets/images/playerstat_twenty_two.png';
import playerstatBigLogo from '../assets/images/playerstat_big_logo.png';
import playerstatBigText from '../assets/images/playerstat_big_text.svg';
import deviceImage from '../assets/images/device_image.png';
import warningIcon from '../assets/images/warning.png';
import Navbar from './Navbar';

// Register GSAP plugins
gsap.registerPlugin(ScrollSmoother, ScrollTrigger);

const Main = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const mainRef = useRef(null);
  
  // Add text reveal animation function
  const createTextReveal = (element, direction = 'up') => {
    if (!element) return;
    
    const text = element.textContent;
    element.textContent = '';
    
    const wrapper = document.createElement('span');
    wrapper.style.display = 'inline-block';
    wrapper.style.overflow = 'hidden';
    
    const inner = document.createElement('span');
    inner.textContent = text;
    inner.style.display = 'inline-block';
    
    // Set initial transform based on direction
    switch(direction) {
      case 'left':
        inner.style.transform = 'translateX(-100%)';
        break;
      case 'right':
        inner.style.transform = 'translateX(100%)';
        break;
      case 'up':
      default:
        inner.style.transform = 'translateY(100%)';
        break;
    }
    
    wrapper.appendChild(inner);
    element.appendChild(wrapper);
    
    return { wrapper, inner, direction };
  };

  useEffect(() => {
    // Create ScrollSmoother instance
    const smoother = ScrollSmoother.create({
      wrapper: "#smooth-wrapper",
      content: "#smooth-content",
      smooth: 1.5,
      effects: true,
      normalizeScroll: true,
      smoothTouch: 0.1,
    });

    // Create ScrollTrigger for navbar
    ScrollTrigger.create({
      start: "top top",
      end: "max",
      onUpdate: (self) => {
        const navbar = document.querySelector('.navbar');
        if (navbar) {
          if (self.direction === 1 && self.progress > 0.1) {
            navbar.classList.add('navbar-scrolled');
          } else {
            navbar.classList.remove('navbar-scrolled');
          }
        }
      }
    });

    // Add image fade-in animations
    const imageElements = document.querySelectorAll('.hero-image, .second-image, .third-image, .fourth-image, .fifth-image, .sixth-image, .seventh-image, .eighth-image, .nineth-image, .tenth-image, .eleventh-image, .twelveth-image, .eighteenth-image, .twenty-image, .twenty-one-image, .twenty-two-image');

    // Set initial state for images
    gsap.set(imageElements, {
      opacity: 0,
      y: 50
    });

    // Create animations for each image
    imageElements.forEach((image) => {
      ScrollTrigger.create({
        trigger: image,
        start: "top 85%",
        end: "bottom 20%",
        onEnter: () => {
          gsap.to(image, {
            opacity: 1,
            y: 0,
            duration: 1.2,
            ease: "power3.out"
          });
        },
        onLeaveBack: () => {
          // Only fade out when the element is fully out of view
          ScrollTrigger.create({
            trigger: image,
            start: "bottom top",
            end: "top bottom",
            onLeave: () => {
              gsap.to(image, {
                opacity: 0,
                y: 50,
                duration: 0.8,
                ease: "power3.in"
              });
            }
          });
        },
        onEnterBack: () => {
          gsap.to(image, {
            opacity: 1,
            y: 0,
            duration: 1.2,
            ease: "power3.out"
          });
        }
      });
    });

    // Add text reveal animations with different directions
    const textAnimations = [
      { selector: '.second-title, .second-description', direction: 'left' },
      { selector: '.third-title, .third-description', direction: 'right' },
      { selector: '.fourth-title, .fourth-description', direction: 'up' },
      { selector: '.fifth-title, .fifth-description', direction: 'left' },
      { selector: '.sixth-title, .sixth-description', direction: 'right' },
      { selector: '.seventh-title, .seventh-description', direction: 'up' },
      { selector: '.eighth-title, .eighth-description', direction: 'left' },
      { selector: '.nineth-title, .nineth-description', direction: 'right' },
      { selector: '.eleventh-title, .eleventh-description', direction: 'up' },
      { selector: '.twelveth-title, .twelveth-description', direction: 'left' },
      { selector: '.eighteenth-title, .eighteenth-subtitle', direction: 'right' },
      { selector: '.twenty-title, .twenty-description', direction: 'up' },
      { selector: '.twenty-one-title, .twenty-one-description', direction: 'left' }
    ];

    textAnimations.forEach(({ selector, direction }) => {
      const elements = document.querySelectorAll(selector);
      elements.forEach((element) => {
        const { wrapper, inner, direction: animDirection } = createTextReveal(element, direction);
        
        ScrollTrigger.create({
          trigger: element,
          start: "top 80%",
          end: "bottom 20%",
          onEnter: () => {
            gsap.to(inner, {
              x: 0,
              y: 0,
              opacity: 1,
              duration: 1,
              ease: "power3.out"
            });
          },
          onLeaveBack: () => {
            // Only fade out when the element is fully out of view
            ScrollTrigger.create({
              trigger: element,
              start: "bottom top",
              end: "top bottom",
              onLeave: () => {
                gsap.to(inner, {
                  x: animDirection === 'left' ? -100 : animDirection === 'right' ? 100 : 0,
                  y: animDirection === 'up' ? 100 : 0,
                  opacity: 0,
                  duration: 0.5,
                  ease: "power3.in"
                });
              }
            });
          },
          onEnterBack: () => {
            gsap.to(inner, {
              x: 0,
              y: 0,
              opacity: 1,
              duration: 1,
              ease: "power3.out"
            });
          }
        });
      });
    });

    return () => {
      smoother.kill();
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  // Auto slide functionality
  useEffect(() => {
    let interval;
    if (isAutoPlaying) {
      interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 5000); // Change slide every 5 seconds
    }
    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  // Pause auto-play when user interacts
  const handleSlideChange = (direction) => {
    setIsAutoPlaying(false);
    if (direction === 'next') {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    } else {
      setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    }
  };

  // Handle dot navigation
  const goToSlide = (index) => {
    setIsAutoPlaying(false);
    setCurrentSlide(index);
  };

  const slides = [
    {
      section: "thirteenth-section",
      image: playerstatThirteenth,
      content: (
        <div className="suggestion-stack">
          <div className="suggestion-box">
            <span className="suggestion-text">SUGGESTION</span>
          </div>
          <div className="suggestion-detail-box">
            <span className="suggestion-detail-text">Player #14's sprint speed dropped by 18% in the last 10 mins.</span>
            <span className="suggestion-detail-text">Recommended sub: #22 (fresh legs, 92% duel win rate in training)</span>
          </div>
          <p className="coach-alert-text">Alerts set by the coach to notify when a players fatigue increases</p>
          <h1 className="fatigue-title">Fatigue alerts</h1>
        </div>
      )
    },
    {
      section: "fourteenth-section",
      image: playerstatFourteenth,
      content: (
        <div className="impact-stack">
          <div className="impact-alert-box">
            <img src={warningIcon} alt="Warning" className="warning-icon" />
            <span className="impact-alert-text">IMPACT ALERT</span>
          </div>
          <div className="impact-detail-box">
            <span className="impact-detail-text">Player #8 just took a 12G impact. 92% match to prior concussion patterns. Immediate check recommended.</span>
          </div>
          <p className="impact-alert-desc">Impact alerts to assess the severity of a hit</p>
          <h1 className="injury-title">Injury Statistics</h1>
        </div>
      )
    },
    {
      section: "fifteenth-section",
      image: playerstatFifteenth,
      content: (
        <div className="cumulative-stack">
          <div className="cumulative-alert-box">
            <img src={warningIcon} alt="Warning" className="warning-icon" />
            <span className="cumulative-alert-text">Warning</span>
          </div>
          <div className="cumulative-detail-box">
            <span className="cumulative-detail-text">Player #3 has absorbed 6+ high-G impacts this half (2x baseline). Fatigue/injury risk elevated.</span>
          </div>
          <p className="cumulative-alert-desc">Notifies the coach when a player has taken multiple impacts</p>
          <h1 className="cumulative-title">Cumulative injury alerts</h1>
        </div>
      )
    },
    {
      section: "sixteenth-section",
      image: playerstatSixteenth,
      content: (
        <div className="efficiency-stack">
          <div className="efficiency-alert-box">
            <img src={warningIcon} alt="Warning" className="warning-icon" />
            <span className="efficiency-alert-text">Suggestion</span>
          </div>
          <div className="efficiency-detail-box">
            <span className="efficiency-detail-text">Player #12's crossing accuracy drops 40% under fatigue. Use short corners while he's fresh.</span>
          </div>
          <p className="efficiency-alert-desc">Suggests coach optimum strategies to overcome inefficiencies</p>
          <h1 className="efficiency-title">Player efficiency</h1>
        </div>
      )
    },
    {
      section: "seventeenth-section",
      image: playerstatSeventeenth,
      content: (
        <div className="movement-stack">
          <div className="movement-alert-box">
            <img src={warningIcon} alt="Warning" className="warning-icon" />
            <span className="movement-alert-text">Suggestion</span>
          </div>
          <div className="movement-detail-box">
            <span className="movement-detail-text">Player #10's lateral acceleration patterns abnormal post 68' (possible limp). Recommend visual check.</span>
          </div>
          <p className="movement-alert-desc">Notifies Coach about inconsistent movements possible due to fatigue or impact</p>
          <h1 className="movement-title">Abnormal movement patterns</h1>
        </div>
      )
    }
  ];

  return (
    <div id="smooth-wrapper">
      <Navbar />
      <div id="smooth-content" ref={mainRef}>
        <main className="main">
          <section className="hero-section">
            <img 
              src={playerstatMain} 
              alt="Player Statistics" 
              className="hero-image"
            />
            <div className="content-container">
              {/** Removed per request: hero logo image (className: "logo-image") */}
              {/*
              <img 
                src={playerstatLogo} 
                alt="PlayerStat Logo" 
                className="logo-image"
              />
              */}
              <img 
                src={playerstatText} 
                alt="PlayerStat Text" 
                className="text-image"
              />
              <p className="description-text">
                A non obstructive system that tracks your teams vitals and movements to provide real-time feedback for coaches using AI
              </p>
            </div>
          </section>
          <section className="second-section">
            <img 
              src={playerstatSecond} 
              alt="Player Statistics Second" 
              className="second-image"
            />
            <div className="second-content-container">
              <h1 className="second-title">Your Coach's Third Eye</h1>
              <p className="second-description">An obstruction free Tactical AI system for sports elites</p>
            </div>
          </section>
          <section className="third-section">
            <img 
              src={playerstatThird} 
              alt="Player Statistics Third" 
              className="third-image"
            />
            <div className="third-content-container">
              <h1 className="third-title">Player Module</h1>
              <p className="third-description">On person module that collects and transmits real-time data</p>
            </div>
          </section>
          <section className="fourth-section">
            <img 
              src={playerstatFourth} 
              alt="Player Statistics Fourth" 
              className="fourth-image"
            />
            <div className="fourth-content-container">
              <h1 className="fourth-title">Size matters</h1>
              <p className="fourth-description">Soft and durable Silicone covered Player module</p>
            </div>
          </section>
          <section className="fifth-section">
            <img 
              src={playerstatFifth} 
              alt="Player Statistics Fifth" 
              className="fifth-image"
            />
            <div className="fifth-content-container">
              <h1 className="fifth-title">Players, plays and patterns</h1>
              <p className="fifth-description">Using AI and ML to its full potential</p>
            </div>
          </section>
          <section className="sixth-section">
            <img 
              src={playerstatSixth} 
              alt="Player Statistics Sixth" 
              className="sixth-image"
            />
            <div className="sixth-content-container">
              <h1 className="sixth-title">Artificial Intelligence</h1>
              <p className="sixth-description">A <strong>broad field</strong> that encapsulates the ability to <strong>mimic human-like cognitive functions</strong>. Like understanding, responding and analyzing data</p>
              <div className="sixth-content-gap"></div>
              <h1 className="sixth-title">Machine Learning</h1>
              <p className="sixth-description">A subset of AI that is able to <strong>rapidly analyze</strong> large amounts of <strong>data</strong> and <strong>learn from insights</strong> using <strong>complex algorithms</strong></p>
            </div>
          </section>
          <section className="seventh-section">
            <img 
              src={playerstatSeventh} 
              alt="Player Statistics Seventh" 
              className="seventh-image"
            />
            <div className="seventh-content-container">
              <h1 className="seventh-title">Base Station</h1>
              <p className="seventh-description">A powerful processing unit to receive and compile data to run AI and ML algorithms</p>
            </div>
          </section>
          <section className="eighth-section">
            <video 
              src={require('../assets/images/eighth_section_video.mp4')}
              className="eighth-image"
              autoPlay
              muted
              loop
              playsInline
            />
            <div className="eighth-section-gradient"></div>
            <div className="eighth-content-container">
              <p className="eighth-description">This is where the magic begins</p>
              <h1 className="eighth-title">Using ML to predict vitals</h1>
            </div>
          </section>
          <section className="nineth-section">
            <img 
              src={playerstatNineth} 
              alt="Player Statistics Nineth" 
              className="nineth-image"
            />
            <div className="nineth-content-container">
              <p className="nineth-description">Data correlation using ML algorithms</p>
              <h1 className="nineth-title">How we relate Training module data with Player module data.</h1>
              <p className="nineth-additional-text">The more data that to train on, the more accurate the predictive results get.</p>
            </div>
          </section>
          <section className="tenth-section">
            <img 
              src={playerstatTenth} 
              alt="Player Statistics Tenth" 
              className="tenth-image"
            />
            <div className="tenth-content-container">
              <p className="tenth-description">Only this tiny Player Module is required while playing a match</p>
            </div>
          </section>
          <section className="eleventh-section">
            <video 
              src={require('../assets/images/eleventh_section_video.mp4')}
              className="eleventh-image"
              autoPlay
              muted
              loop
              playsInline
            />
            <div className="eleventh-section-gradient"></div>
            <div className="eleventh-content-container">
              <h1 className="eleventh-title">No injuries, No obstruction, only data.</h1>
              <p className="eleventh-description">PlayerStat AI learns your teams patterns and suggests adjustments live during play</p>
            </div>
          </section>
          <section className="twelveth-section">
            <img 
              src={playerstatTwelveth} 
              alt="Player Statistics Twelveth" 
              className="twelveth-image"
            />
            <div className="twelveth-content-container">
              <h1 className="twelveth-title">Actionable insights using AI</h1>
              <p className="twelveth-description">Real-time data that is relayed in a digestible fashion</p>
            </div>
          </section>
          <section className="carousel-container">
            <button className="carousel-button prev" onClick={() => handleSlideChange('prev')}>❮</button>
            <section className={slides[currentSlide].section}>
              <img 
                src={slides[currentSlide].image} 
                alt={`Player Statistics ${slides[currentSlide].section}`} 
                className={`${slides[currentSlide].section}-image`}
              />
              {slides[currentSlide].content}
            </section>
            <button className="carousel-button next" onClick={() => handleSlideChange('next')}>❯</button>
            <div className="carousel-dots">
              {slides.map((_, index) => (
                <button
                  key={index}
                  className={`carousel-dot ${currentSlide === index ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                />
              ))}
            </div>
          </section>
          <section className="eighteenth-section">
            <img 
              src={playerstatEighteenth} 
              alt="Player Statistics Eighteenth" 
              className="eighteenth-image"
            />
            <div className="eighteenth-content-container">
              <h3 className="eighteenth-subtitle">A dashboard of all the vitals of the entire team</h3>
              <h2 className="eighteenth-title">All real time data</h2>
            </div>
          </section>
          <section className="twenty-section">
            <img 
              src={playerstatTwenty} 
              alt="Player Statistics Twenty" 
              className="twenty-image"
            />
            <div className="twenty-content-container">
              <h1 className="twenty-title">Post game analysis</h1>
              <p className="twenty-description">Just the beginning of something bigger.</p>
            </div>
          </section>
          <section className="twenty-one-section">
            <img 
              src={playerstatTwentyOne} 
              alt="Player Statistics Twenty One" 
              className="twenty-one-image"
            />
            <div className="twenty-one-content-container">
              <h1 className="twenty-one-title">We aren't your coaches</h1>
              <p className="twenty-one-description">We're just their third eye</p>
            </div>
          </section>
          <section className="twenty-two-section">
            <img 
              src={playerstatTwentyTwo} 
              alt="Player Statistics Twenty Two" 
              className="twenty-two-image"
            />
            <div className="twenty-two-content-container">
              {/** Removed per request: big logo image (className: "big-logo-image") */}
              {/*
              <img 
                src={playerstatBigLogo} 
                alt="PlayerStat Big Logo" 
                className="big-logo-image"
              />
              */}
              <img 
                src={playerstatBigText} 
                alt="PlayerStat Big Text" 
                className="big-text-image"
              />
              <img 
                src={deviceImage} 
                alt="Device Image" 
                className="device-image"
              />
              <div className="text-wrapper">
                <h3 className="company-text">PlayerStat AI Private Limited</h3>
                <h3 className="website-text">www.playerstat.ai</h3>
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default Main; 